import React, { useState, useEffect, useCallback, useMemo } from "react";
import { cn } from "../../../lib/utils";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import type { NodeProps, Node, Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Network, User, Bot, Wrench, RefreshCw } from "lucide-react";
import { Button } from "../../../ui/button";
import { GatewayAuditApi } from "../Api/gatewayApi";
import { apiError } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import { IdentityGraph, GraphNode, GraphLink } from "../interfaces/gatewayInterfaces";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import "../AgenticGateway.css";

const WINDOWS = [
  { label: "Last 24h", value: "24" },
  { label: "Last 7 days", value: "168" },
  { label: "Last 30 days", value: "720" },
  { label: "All time", value: "" },
];

const COLUMN_X: Record<string, number> = { HUMAN: 0, AGENT: 360, TOOL: 720 };
const ROW_GAP = 96;

function typeIcon(type: string) {
  if (type === "HUMAN") return <User className="w-3.5 h-3.5" />;
  if (type === "AGENT") return <Bot className="w-3.5 h-3.5" />;
  return <Wrench className="w-3.5 h-3.5" />;
}
function typeIconBg(type: string) {
  if (type === "HUMAN") return "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400";
  if (type === "AGENT") return "bg-primary/15 text-primary";
  return "bg-teal-500/15 text-teal-600 dark:text-teal-400";
}

// invoked edges are colored by decision; acts-through is purple
function edgeColor(link: GraphLink): string {
  if (link.type === "acts-through") return "#a78bfa";
  if (link.denied > 0 && link.allowed === 0) return "#f87171"; // all denied
  if (link.denied > 0 && link.allowed > 0) return "#fbbf24"; // mixed
  if (link.allowed > 0) return "#34d399"; // all allowed
  return "#64748b";
}

function GraphNodeCard({ data }: NodeProps) {
  const d: any = data;
  return (
    <div className={cn("rounded-lg border px-3 py-2 min-w-[150px] max-w-[210px] shadow-sm bg-card", d.borderClass)}>
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground/40 !border-0" />
      <div className="flex items-center gap-2">
        <div className={cn("w-6 h-6 rounded-md flex items-center justify-center shrink-0", typeIconBg(d.nodeType))}>
          {typeIcon(d.nodeType)}
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold text-foreground truncate" title={d.label}>{d.label}</div>
          {d.sublabel && <div className="text-[10px] text-muted-foreground truncate">{d.sublabel}</div>}
        </div>
      </div>
      {typeof d.requests === "number" && (
        <div className="mt-1 text-[10px] text-muted-foreground">{d.requests} req</div>
      )}
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground/40 !border-0" />
    </div>
  );
}

const nodeTypes = { identity: GraphNodeCard };

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-lg ag-surface border border-border px-3 py-1.5 flex items-center gap-2">
      <span className={cn("text-sm font-bold", color)}>{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function GraphInner() {
  const { toast } = useToast();
  const [hours, setHours] = useState("24");
  const [graph, setGraph] = useState<IdentityGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoverId, setHoverId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GatewayAuditApi.graph(hours);
      setGraph({ nodes: data?.nodes || [], links: data?.links || [] });
    } catch (e: any) {
      toast({ title: "Failed to load identity graph", description: apiError(e), variant: "destructive" });
      setGraph({ nodes: [], links: [] });
    } finally {
      setLoading(false);
    }
  }, [hours, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const { rfNodes, rfEdges, counts, adj } = useMemo(() => {
    const nodes = graph?.nodes || [];
    const links = graph?.links || [];
    const byType: Record<string, GraphNode[]> = { HUMAN: [], AGENT: [], TOOL: [] };
    nodes.forEach((n) => {
      (byType[n.type] || (byType[n.type] = [])).push(n);
    });
    Object.values(byType).forEach((arr) => arr.sort((a, b) => (b.requests || 0) - (a.requests || 0)));

    const rfNodes: Node[] = [];
    (["HUMAN", "AGENT", "TOOL"] as const).forEach((type) => {
      (byType[type] || []).forEach((n, i) => {
        const isUnattributed = type === "AGENT" && (!n.label || n.label === "unknown");
        rfNodes.push({
          id: n.id,
          type: "identity",
          position: { x: COLUMN_X[type], y: i * ROW_GAP },
          data: {
            label: isUnattributed ? "unattributed" : n.label,
            sublabel: n.sublabel,
            requests: n.requests,
            nodeType: type,
            borderClass: isUnattributed ? "border-amber-500/40" : "border-border",
          },
          draggable: true,
          connectable: false,
        });
      });
    });

    // adjacency: for each node, the set of connected node ids + edge ids
    const adj: Record<string, { nodes: Set<string>; edges: Set<string> }> = {};
    const ensure = (id: string) => (adj[id] || (adj[id] = { nodes: new Set(), edges: new Set() }));

    const rfEdges: Edge[] = links.map((l, i) => {
      const color = edgeColor(l);
      const eid = `e-${i}-${l.source}-${l.target}`;
      ensure(l.source).nodes.add(l.target);
      ensure(l.target).nodes.add(l.source);
      ensure(l.source).edges.add(eid);
      ensure(l.target).edges.add(eid);
      return {
        id: eid,
        source: l.source,
        target: l.target,
        animated: l.type === "acts-through",
        style: { stroke: color, strokeWidth: Math.max(1, Math.log2((l.count || 1) + 1)) },
        label: l.type === "invoked" ? `${l.allowed}✓${l.denied > 0 ? ` ${l.denied}✕` : ""}` : undefined,
        labelStyle: { fill: "#94a3b8", fontSize: 9 },
        labelBgStyle: { fill: "transparent" },
      } as Edge;
    });

    const counts = {
      humans: (byType.HUMAN || []).length,
      agents: (byType.AGENT || []).length,
      tools: (byType.TOOL || []).length,
      allowed: links.reduce((s, l) => s + (l.allowed || 0), 0),
      denied: links.reduce((s, l) => s + (l.denied || 0), 0),
    };
    return { rfNodes, rfEdges, counts, adj };
  }, [graph]);

  // Own the nodes/edges so React Flow persists each node's measured size (via onNodesChange). Fully-controlled
  // props recomputed on hover forced a re-measure every time, which made the edges flash — the reported flicker.
  const [nodes, setNodes, onNodesChange] = useNodesState(rfNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(rfEdges);

  // Reload the graph only when new data arrives — never on hover.
  useEffect(() => {
    setNodes(rfNodes);
    setEdges(rfEdges);
    setHoverId(null);
  }, [rfNodes, rfEdges, setNodes, setEdges]);

  // Hover-to-highlight: emphasise the hovered node + its neighbours/edges, dim the rest. Nodes map over the
  // CURRENT (already-measured) state so dimensions are preserved and the edges no longer flicker; edges are
  // restyled from the base set so each keeps its own width (log-scaled by call volume).
  useEffect(() => {
    const near = hoverId ? adj[hoverId] : null;
    setNodes((nds) =>
      nds.map((n) => {
        const active = !near || n.id === hoverId || near.nodes.has(n.id);
        return { ...n, style: { ...(n.style || {}), opacity: active ? 1 : 0.2, transition: "opacity 120ms" } } as Node;
      })
    );
    setEdges(
      rfEdges.map((e) => {
        const active = near ? near.edges.has(e.id) : false;
        const baseW = (e.style as any)?.strokeWidth || 1;
        return {
          ...e,
          animated: near ? active && (e.animated as boolean) : (e.animated as boolean),
          style: {
            ...(e.style || {}),
            opacity: near ? (active ? 1 : 0.08) : 1,
            strokeWidth: near && active ? Math.max(2, baseW + 1) : baseW,
          },
        } as Edge;
      })
    );
  }, [hoverId, rfEdges, adj, setNodes, setEdges]);

  return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar ag-nav">
        <div className="cea-tabs ag-tabs" role="tablist" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              type="button"
              role="tab"
              aria-selected={hours === w.value}
              onClick={() => setHours(w.value)}
              className={cn("cea-tab", hours === w.value && "active")}
            >
              {w.label}
            </button>
          ))}
        </div>
        <div className="ag-toolbar-group">
          <span className="ag-summary" title="Who acts through whom, and what they invoke — across sessions and stateless requests.">
            <Network aria-hidden="true" />
            <span>Identity relationships</span>
          </span>
          <Button variant="outline" onClick={load} disabled={loading} className="ag-button no-default-hover-elevate">
            <RefreshCw className={cn(loading && "animate-spin")} /> Refresh
          </Button>
        </div>
      </div>

      <div className="ag-card ag-content min-h-[460px]">
        <div className="ag-card-header ag-card-header-bordered">
          <div className="flex items-center gap-2 flex-wrap">
            <StatPill label="Humans" value={counts.humans} color="text-indigo-600 dark:text-indigo-400" />
            <StatPill label="Agents" value={counts.agents} color="text-primary" />
            <StatPill label="Tools" value={counts.tools} color="text-teal-600 dark:text-teal-400" />
            <StatPill label="Allowed" value={counts.allowed} color="text-emerald-600 dark:text-emerald-400" />
            <StatPill label="Denied" value={counts.denied} color="text-red-600 dark:text-red-400" />
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-violet-400 inline-block" /> acts-through</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block" /> allowed</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400 inline-block" /> mixed</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-400 inline-block" /> denied</span>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          {loading ? (
            <div className="ag-loading h-full">
              <span className="ag-spinner animate-spin" aria-hidden="true" />
              <span>Loading graph…</span>
            </div>
          ) : rfNodes.length === 0 ? (
            <div className="ag-empty h-full">
              <span className="ag-empty-icon" aria-hidden="true">
                <Network />
              </span>
              <p>No activity in this window</p>
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              proOptions={{ hideAttribution: true }}
              nodesConnectable={false}
              edgesFocusable={false}
              onNodeMouseEnter={(_, node) => setHoverId(node.id)}
              onNodeMouseLeave={() => setHoverId(null)}
            >
              <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="rgba(148,163,184,0.15)" />
              <Controls showInteractive={false} />
            </ReactFlow>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GatewayIdentityGraphPage() {
  return (
    <ReactFlowProvider>
      <GraphInner />
    </ReactFlowProvider>
  );
}
